import { FC, MouseEvent, useContext, useMemo } from "react";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import { useCrudNavigateAdmin, usePageContext } from "@ryde/redux";
import { IStyledAttributes, RydeAdminUnit } from "@ryde/shared";

import { useDecorateWithMark } from "../../../utils";
import { TableWrapperContext } from "../table/table-wrapper.context";
import { EditLinkAnchorStyled, EditLinkStyled } from "./cell-link.styled";

const IdColumnEditGenericStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "id-column-edit",
})`
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export interface ICellIdEdit {
  id: string | number;
}

export interface ICellIdWebEdit extends ICellIdEdit {
  onEditLinkClick: (id: number) => void;
}

/**
 * Clicks the browser should handle itself: ctrl/cmd → new tab, shift → new window, non-left button.
 * `altKey` is deliberately NOT here — the native alt+click action is "download link target", which on
 * this hash router would download index.html instead of opening the edit form.
 */
const isModifiedClickEvent = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0;

export const IdColumnEditGeneric: FC<ICellIdWebEdit> = ({
  id,
  onEditLinkClick,
}) => {
  const { decorateValue } = useDecorateWithMark();
  const { idColumnEditHrefBase, idColumnEditHrefSearch } =
    useContext(TableWrapperContext);

  const highlightedId = useMemo(() => {
    return decorateValue(String(id));
  }, [id, decorateValue]);

  const handleClick = useEvent(() => {
    onEditLinkClick(+id);
  });

  const handleAnchorClick = useEvent((event: MouseEvent<HTMLAnchorElement>) => {
    // Modified clicks (ctrl/cmd/shift/middle) keep the browser's native new-tab handling.
    if (isModifiedClickEvent(event)) {
      return;
    }

    // Plain left click keeps today's imperative flow (form-state resets, single-select
    // selection) so navigation semantics are unchanged; the href exists for the browser.
    event.preventDefault();
    onEditLinkClick(+id);
  });

  if (idColumnEditHrefBase) {
    return (
      <IdColumnEditGenericStyled className="editblock">
        <EditLinkAnchorStyled
          to={`${idColumnEditHrefBase}${id}${idColumnEditHrefSearch}`}
          data-e2e-id={id}
          onClick={handleAnchorClick}
        >
          <span>{highlightedId}</span>
        </EditLinkAnchorStyled>
      </IdColumnEditGenericStyled>
    );
  }

  return (
    <IdColumnEditGenericStyled className="editblock">
      <EditLinkStyled data-e2e-id={id} onClick={handleClick}>
        <span>{highlightedId}</span>
      </EditLinkStyled>
    </IdColumnEditGenericStyled>
  );
};

interface ICellIdEditAdmin extends ICellIdEdit {
  disableNavigation: boolean;
}

export const IdColumnEditGenericAdmin: FC<ICellIdEditAdmin> = ({
  id,
  disableNavigation,
}) => {
  const { decorateValue } = useDecorateWithMark();
  const { entity } = usePageContext();
  const { navigateToEdit } = useCrudNavigateAdmin(entity as RydeAdminUnit);

  const highlightedId = useMemo(() => {
    return decorateValue(String(id));
  }, [id, decorateValue]);

  const onEditLinkClick = useEvent(() => {
    if (disableNavigation) {
      return;
    }

    navigateToEdit(+id);
  });

  return (
    <IdColumnEditGenericStyled className="editblock">
      <EditLinkStyled data-e2e-id={id} onClick={onEditLinkClick}>
        <span>{highlightedId}</span>
      </EditLinkStyled>
    </IdColumnEditGenericStyled>
  );
};
